
import React, { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CartProvider } from './contexts/CartContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { checkDomainRedirect } from './utils/seo';
import { useDynamicSEO } from './hooks/useDynamicSEO';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import FooterV2 from './components/FooterV2';
import PhotoManager from './components/PhotoManager';
import MobileBottomNav from './components/MobileBottomNav';

// Pages
import HomePage from './pages/HomePage';
import PhotoEditPage from './pages/PhotoEditPage';
import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import StorePage from './pages/StorePage';
import StoreDetailPage from './pages/StoreDetailPage';
import XinyiStorePage from './pages/XinyiStorePage';
import LabPage from './pages/LabPage';
import RoastLabPage from './pages/RoastLabPage';
import PartnersPage from './pages/PartnersPage';
import WholesalePage from './pages/WholesalePage';
import ArticlePage from './pages/ArticlePage';
import AdminDashboard from './pages/AdminDashboard';
import MemberPage from './pages/MemberPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SitemapPage from './pages/SitemapPage';
import EzShipCallbackPage from './pages/EzShipCallbackPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import FAQPage from './pages/FAQPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import MarketingPlanPage from './pages/MarketingPlanPage';
import SystemUpdate2026QPage from './pages/SystemUpdate2026QPage';
import HomePageV2 from './pages/HomePageV2';
import BusinessPageV2 from './pages/BusinessPageV2';
import ShopBeansV2Page from './pages/ShopBeansV2Page';
import StoreV2Page from './pages/StoreV2Page';
import XinyiStoreV2Page from './pages/XinyiStoreV2Page';
import WholesaleV2Page from './pages/WholesaleV2Page';
import ProductPageV2 from './pages/ProductPageV2';
import LabPageV2 from './pages/LabPageV2';
import ArticlePageV2 from './pages/ArticlePageV2';
import RoastLabPageV2 from './pages/RoastLabPageV2';
import PartnersPageV2 from './pages/PartnersPageV2';
import StoreDetailV2Page from './pages/StoreDetailV2Page';
import MemberV2Page from './pages/MemberV2Page';
import FAQV2Page from './pages/FAQV2Page';
import PrivacyV2Page from './pages/PrivacyV2Page';
import TermsV2Page from './pages/TermsV2Page';
import SitemapV2Page from './pages/SitemapV2Page';
import MarketingPlanV2Page from './pages/MarketingPlanV2Page';
import SystemUpdate2026QV2Page from './pages/SystemUpdate2026QV2Page';
import OrderSuccessV2Page from './pages/OrderSuccessV2Page';
import OrderTrackingV2Page from './pages/OrderTrackingV2Page';
import PaymentSuccessV2Page from './pages/PaymentSuccessV2Page';
import PaymentCancelV2Page from './pages/PaymentCancelV2Page';
import CartV2Page from './pages/CartV2Page';
import CheckoutV2Page from './pages/CheckoutV2Page';

// 🔄 自動滾動到頂部組件
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // 使用多種方式確保滾動到頂部
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // 延遲執行以確保 DOM 更新後也能滾動
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
  }, [pathname]);

  return null;
}

function App() {
  const location = useLocation();

  // 🌐 檢測是否為靜態頁面
  const isStaticPage = (window as any).__STATIC_PAGE__ || false;
  const staticLang = (window as any).__STATIC_LANG__ || 'zh';
  const staticPath = (window as any).__STATIC_PATH__ || '';

  // 🔍 動態載入 SEO（僅在非靜態頁面使用）
  // 🔧 暫時啟用，已添加安全檢查
  useDynamicSEO();

  useEffect(() => {
    // 🌐 檢查域名重定向
    checkDomainRedirect();
  }, []);

  // 🌐 根據當前路由動態決定語言
  const currentLang = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/en')) {
      return 'en';
    }
    return 'zh'; // 預設中文
  }, [location.pathname]);

  // 🌐 如果是靜態頁面，直接渲染對應頁面（不使用 Router）
  if (isStaticPage && staticPath === '/zh/store/xinyi') {
    return (
      <ThemeProvider>
        <CartProvider>
          <>
            <Helmet htmlAttributes={{ lang: 'zh' }} />
            <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
              <Header />
              <main>
                <XinyiStorePage />
              </main>
              <Footer />
            </div>
          </>
        </CartProvider>
      </ThemeProvider>
    );
  }

  if (isStaticPage && staticPath === '/en/store/xinyi') {
    return (
      <ThemeProvider>
        <CartProvider>
          <>
            <Helmet htmlAttributes={{ lang: 'en' }} />
            <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
              <Header />
              <main>
                <XinyiStorePage />
              </main>
              <Footer />
            </div>
          </>
        </CartProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <CartProvider>
        <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
          {/* 🌐 動態設置 <html lang> 屬性 */}
          <Helmet htmlAttributes={{ lang: currentLang }} />
          
          {/* 🔄 頁面切換自動滾動到頂部 */}
          <ScrollToTop />
          
          {/* 📱 手機版底部導航欄 */}
          <MobileBottomNav />
          
          <Routes>
            {/* 會員中心 */}
            <Route path="/member" element={<Navigate to={`/${currentLang}/member`} replace />} />
            <Route path="/zh/member/*" element={<><Header /><main><MemberPage /></main><Footer /></>} />
            <Route path="/en/member/*" element={<><Header /><main><MemberPage /></main><Footer /></>} />

            {/* 管理後台 - 不使用 Header/Footer */}
            <Route path="/admin/*" element={<AdminDashboard />} />
            
            {/* 前台頁面 - 使用 Header/Footer */}
            {/* 多語系主要頁面 - 根路徑重定向到中文 */}
            <Route path="/" element={<Navigate to="/zh" replace />} />
            {/* 🦴 換骨：舊 URL 直接 render V2 畫面（保留 SEO，只換外觀） */}
            <Route path="/zh" element={<><main><HomePageV2 /></main><FooterV2 /></>} />
            <Route path="/en" element={<><main><HomePageV2 /></main><FooterV2 /></>} />
            
            {/* 🦴 換骨：商店頁面（ShopBeansV2Page 自帶 HeaderV2 + FooterV2） */}
            <Route path="/shop" element={<ShopBeansV2Page />} />
            <Route path="/zh/shop" element={<ShopBeansV2Page />} />
            <Route path="/en/shop" element={<ShopBeansV2Page />} />
            <Route path="/shop/:category" element={<ShopBeansV2Page />} />
            <Route path="/zh/shop/:category" element={<ShopBeansV2Page />} />
            <Route path="/en/shop/:category" element={<ShopBeansV2Page />} />
            
            {/* 🦴 換骨：商品詳細頁（V2 自帶完整 SEO 結構，舊 ProductPage 保留作為 fallback） */}
            <Route path="/product/:id" element={<><Header /><main><ProductPage /></main><Footer /></>} />
            <Route path="/zh/product/:id" element={<><Header /><main><ProductPage /></main><Footer /></>} />
            <Route path="/en/product/:id" element={<><Header /><main><ProductPage /></main><Footer /></>} />
            <Route path="/shop/:category/:id/:slug" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/shop/:category/:id/:slug" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/shop/:category/:id/:slug" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />
            
            {/* 購物車和結帳 */}
            <Route path="/cart" element={<><Header /><main><CartPage /></main><Footer /></>} />
            <Route path="/zh/cart" element={<><Header /><main><CartPage /></main><Footer /></>} />
            <Route path="/en/cart" element={<><Header /><main><CartPage /></main><Footer /></>} />
            <Route path="/checkout" element={<><Header /><main><CheckoutPage /></main><Footer /></>} />
            <Route path="/zh/checkout" element={<><Header /><main><CheckoutPage /></main><Footer /></>} />
            <Route path="/en/checkout" element={<><Header /><main><CheckoutPage /></main><Footer /></>} />
            <Route path="/order-success" element={<><Header /><main><OrderSuccessPage /></main><Footer /></>} />
            <Route path="/zh/order-success" element={<><Header /><main><OrderSuccessPage /></main><Footer /></>} />
            <Route path="/en/order-success" element={<><Header /><main><OrderSuccessPage /></main><Footer /></>} />
            
            {/* ezShip 電子地圖回傳頁 - 不使用 Header/Footer（彈出視窗） */}
            <Route path="/ezship-callback" element={<EzShipCallbackPage />} />
            <Route path="/zh/ezship-callback" element={<EzShipCallbackPage />} />
            <Route path="/en/ezship-callback" element={<EzShipCallbackPage />} />
            
            {/* 🦴 換骨：門市總覽頁（StoreV2Page 自帶 HeaderV2，外加 FooterV2） */}
            <Route path="/store" element={<><main><StoreV2Page /></main><FooterV2 /></>} />
            <Route path="/zh/store" element={<><main><StoreV2Page /></main><FooterV2 /></>} />
            <Route path="/en/store" element={<><main><StoreV2Page /></main><FooterV2 /></>} />
            {/* 🦴 換骨：信義店專屬頁面（XinyiStoreV2Page 自帶 HeaderV2 + FooterV2 視覺） */}
            <Route path="/store/xinyi" element={<><main><XinyiStoreV2Page /></main><FooterV2 /></>} />
            <Route path="/zh/store/xinyi" element={<><main><XinyiStoreV2Page /></main><FooterV2 /></>} />
            <Route path="/en/store/xinyi" element={<><main><XinyiStoreV2Page /></main><FooterV2 /></>} />
            {/* 🦴 換骨：其他分店動態路由（StoreDetailV2Page 殼換 V2，內容沿用 StoreDetailPage） */}
            <Route path="/store/:slug" element={<main><StoreDetailV2Page /></main>} />
            <Route path="/zh/store/:slug" element={<main><StoreDetailV2Page /></main>} />
            <Route path="/en/store/:slug" element={<main><StoreDetailV2Page /></main>} />
            
            {/* 🦴 換骨：Lab 文章列表（LabPageV2 殼內嵌 LabPage，SEO 由內嵌頁接管） */}
            <Route path="/lab" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/lab" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/lab" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/lab/:category" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/lab/:category" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/lab/:category" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            {/* 🦴 換骨：Article 內頁（ArticlePageV2 殼內嵌 ArticlePage，SEO 由內嵌頁接管） */}
            <Route path="/lab/:category/:slug" element={<><main><ArticlePageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/lab/:category/:slug" element={<><main><ArticlePageV2 /></main><FooterV2 /></>} />
            <Route path="/en/lab/:category/:slug" element={<><main><ArticlePageV2 /></main><FooterV2 /></>} />
            {/* 🦴 換骨：烘豆實驗室（RoastLabPageV2 自帶 V2 視覺） */}
            <Route path="/roast-lab" element={<><main><RoastLabPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/roast-lab" element={<><main><RoastLabPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/roast-lab" element={<><main><RoastLabPageV2 /></main><FooterV2 /></>} />
            
            {/* 🦴 換骨：合作夥伴 + 批發 */}
            <Route path="/partners" element={<><main><PartnersPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/partners" element={<><main><PartnersPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/partners" element={<><main><PartnersPageV2 /></main><FooterV2 /></>} />
            <Route path="/wholesale" element={<><main><WholesaleV2Page /></main><FooterV2 /></>} />
            <Route path="/zh/wholesale" element={<><main><WholesaleV2Page /></main><FooterV2 /></>} />
            <Route path="/en/wholesale" element={<><main><WholesaleV2Page /></main><FooterV2 /></>} />
            
            {/* 內容頁面 */}
            <Route path="/articles/:id" element={<><Header /><main><ArticlePage /></main><Footer /></>} />
            <Route path="/zh/articles/:id" element={<><Header /><main><ArticlePage /></main><Footer /></>} />
            <Route path="/en/articles/:id" element={<><Header /><main><ArticlePage /></main><Footer /></>} />
            
            {/* 重定向舊路徑 */}
            <Route path="/products" element={<Navigate to="/shop" replace />} />
            <Route path="/zh/products" element={<Navigate to="/zh/shop" replace />} />
            <Route path="/en/products" element={<Navigate to="/en/shop" replace />} />
            <Route path="/coffee" element={<Navigate to="/shop" replace />} />
            <Route path="/zh/coffee" element={<Navigate to="/zh/shop" replace />} />
            <Route path="/en/coffee" element={<Navigate to="/en/shop" replace />} />
            
            {/* 隱私政策與服務條款 */}
            <Route path="/privacy" element={<><Header /><main><PrivacyPolicyPage /></main><Footer /></>} />
            <Route path="/zh/privacy" element={<><Header /><main><PrivacyPolicyPage /></main><Footer /></>} />
            <Route path="/en/privacy" element={<><Header /><main><PrivacyPolicyPage /></main><Footer /></>} />
            <Route path="/terms" element={<><Header /><main><TermsOfServicePage /></main><Footer /></>} />
            <Route path="/zh/terms" element={<><Header /><main><TermsOfServicePage /></main><Footer /></>} />
            <Route path="/en/terms" element={<><Header /><main><TermsOfServicePage /></main><Footer /></>} />
            
            {/* 網站地圖 */}
            <Route path="/sitemap" element={<><Header /><main><SitemapPage /></main><Footer /></>} />
            <Route path="/zh/sitemap" element={<><Header /><main><SitemapPage /></main><Footer /></>} />
            <Route path="/en/sitemap" element={<><Header /><main><SitemapPage /></main><Footer /></>} />
            
            {/* 訂單查詢 */}
            <Route path="/order-tracking" element={<><Header /><main><OrderTrackingPage /></main><Footer /></>} />
            <Route path="/zh/order-tracking" element={<><Header /><main><OrderTrackingPage /></main><Footer /></>} />
            <Route path="/en/order-tracking" element={<><Header /><main><OrderTrackingPage /></main><Footer /></>} />
            
            {/* FAQ */}
            <Route path="/faq" element={<><Header /><main><FAQPage /></main><Footer /></>} />
            <Route path="/zh/faq" element={<><Header /><main><FAQPage /></main><Footer /></>} />
            <Route path="/en/faq" element={<><Header /><main><FAQPage /></main><Footer /></>} />
            
            {/* 藍新 NewebPay 付款結果頁面 */}
            <Route path="/payment-success" element={<><Header /><main><PaymentSuccessPage /></main><Footer /></>} />
            <Route path="/zh/payment-success" element={<><Header /><main><PaymentSuccessPage /></main><Footer /></>} />
            <Route path="/en/payment-success" element={<><Header /><main><PaymentSuccessPage /></main><Footer /></>} />
            <Route path="/payment-cancel" element={<><Header /><main><PaymentCancelPage /></main><Footer /></>} />
            <Route path="/zh/payment-cancel" element={<><Header /><main><PaymentCancelPage /></main><Footer /></>} />
            <Route path="/en/payment-cancel" element={<><Header /><main><PaymentCancelPage /></main><Footer /></>} />

            {/* 2026 行銷計畫 */}
            <Route path="/zh/2026-marketing-plan" element={<><Header /><main><MarketingPlanPage /></main><Footer /></>} />

            {/* 2026Q 系統升級藍圖 */}
            <Route path="/zh/system-update-2026Q" element={<><Header /><main><SystemUpdate2026QPage /></main><Footer /></>} />

            {/* V2 預覽 - Barista Map 風格首頁（使用 HeaderV2 + FooterV2，不套用舊 Header/Footer） */}
            <Route path="/zh/v2" element={<><main><HomePageV2 /></main><FooterV2 /></>} />
            <Route path="/en/v2" element={<><main><HomePageV2 /></main><FooterV2 /></>} />

            {/* 🦴 Our Business 頁（V2 新增專頁，沒有舊版對應，舊 /v2 路由作為預覽保留） */}
            <Route path="/zh/business" element={<><main><BusinessPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/business" element={<><main><BusinessPageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - Our Business 頁（門店 / 網購 / 批發） */}
            <Route path="/zh/business/v2" element={<><main><BusinessPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/business/v2" element={<><main><BusinessPageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - Shop Beans（極簡咖啡豆網格，對標 Barista Map）— 元件內已自帶 HeaderV2 + FooterV2 */}
            <Route path="/zh/shop/beans/v2" element={<ShopBeansV2Page />} />
            <Route path="/en/shop/beans/v2" element={<ShopBeansV2Page />} />

            {/* V2 預覽 - Shop 通用分類頁（dripbag / gift / gear...，套用同一極簡版型） */}
            <Route path="/zh/shop/v2" element={<ShopBeansV2Page />} />
            <Route path="/en/shop/v2" element={<ShopBeansV2Page />} />
            <Route path="/zh/shop/:category/v2" element={<ShopBeansV2Page />} />
            <Route path="/en/shop/:category/v2" element={<ShopBeansV2Page />} />

            {/* V2 預覽 - Store 總覽（雙欄門市，對標 Barista Map locations） */}
            <Route path="/zh/store/v2" element={<><main><StoreV2Page /></main><FooterV2 /></>} />
            <Route path="/en/store/v2" element={<><main><StoreV2Page /></main><FooterV2 /></>} />

            {/* V2 預覽 - 信義分店（Barista Map 風格內頁，舊 /store/xinyi 保留不動） */}
            <Route path="/zh/store/xinyi/v2" element={<><main><XinyiStoreV2Page /></main><FooterV2 /></>} />
            <Route path="/en/store/xinyi/v2" element={<><main><XinyiStoreV2Page /></main><FooterV2 /></>} />

            {/* V2 預覽 - 批發 Wholesale（Barista Map 風格，舊 /wholesale 保留不動以維持 SEO） */}
            <Route path="/zh/wholesale/v2" element={<><main><WholesaleV2Page /></main><FooterV2 /></>} />
            <Route path="/en/wholesale/v2" element={<><main><WholesaleV2Page /></main><FooterV2 /></>} />

            {/* V2 預覽 - 商品詳細頁（Barista Map 風格，舊 /shop/:category/:id/:slug 保留不動以維持 SEO） */}
            <Route path="/zh/shop/:category/v2/:id/:slug" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/shop/:category/v2/:id/:slug" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/shop/:category/v2/:id" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/shop/:category/v2/:id" element={<><main><ProductPageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - Lab 文章列表（noindex，舊 /lab 保留 SEO） */}
            <Route path="/zh/lab/v2" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/lab/v2" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/zh/lab/:category/v2" element={<><main><LabPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/lab/:category/v2" element={<><main><LabPageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - Article 內頁（noindex，舊 /lab/:category/:slug 保留 SEO） */}
            <Route path="/zh/lab/:category/v2/:slug" element={<><main><ArticlePageV2 /></main><FooterV2 /></>} />
            <Route path="/en/lab/:category/v2/:slug" element={<><main><ArticlePageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - Roast Lab 烘豆實驗室（noindex） */}
            <Route path="/zh/roast-lab/v2" element={<><main><RoastLabPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/roast-lab/v2" element={<><main><RoastLabPageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - Partners 合作夥伴（noindex） */}
            <Route path="/zh/partners/v2" element={<><main><PartnersPageV2 /></main><FooterV2 /></>} />
            <Route path="/en/partners/v2" element={<><main><PartnersPageV2 /></main><FooterV2 /></>} />

            {/* V2 預覽 - 其他分店動態路由（殼換 V2，內容沿用 StoreDetailPage；舊路由保留 SEO） */}
            <Route path="/zh/store/:slug/v2" element={<main><StoreDetailV2Page /></main>} />
            <Route path="/en/store/:slug/v2" element={<main><StoreDetailV2Page /></main>} />

            {/* V2 預覽 - 會員中心（殼換 V2，內容沿用 MemberPage；舊 /[lang]/member 不動） */}
            <Route path="/zh/member/v2/*" element={<main><MemberV2Page /></main>} />
            <Route path="/en/member/v2/*" element={<main><MemberV2Page /></main>} />

            {/* V2 預覽 - FAQ（殼換 V2） */}
            <Route path="/zh/faq/v2" element={<main><FAQV2Page /></main>} />
            <Route path="/en/faq/v2" element={<main><FAQV2Page /></main>} />

            {/* V2 預覽 - 隱私政策（殼換 V2） */}
            <Route path="/zh/privacy/v2" element={<main><PrivacyV2Page /></main>} />
            <Route path="/en/privacy/v2" element={<main><PrivacyV2Page /></main>} />

            {/* V2 預覽 - 服務條款（殼換 V2） */}
            <Route path="/zh/terms/v2" element={<main><TermsV2Page /></main>} />
            <Route path="/en/terms/v2" element={<main><TermsV2Page /></main>} />

            {/* V2 預覽 - 網站地圖（殼換 V2） */}
            <Route path="/zh/sitemap/v2" element={<main><SitemapV2Page /></main>} />
            <Route path="/en/sitemap/v2" element={<main><SitemapV2Page /></main>} />

            {/* V2 預覽 - 2026 行銷計畫（內部頁，殼換 V2） */}
            <Route path="/zh/2026-marketing-plan/v2" element={<main><MarketingPlanV2Page /></main>} />

            {/* V2 預覽 - 2026Q 系統升級藍圖（內部頁，殼換 V2） */}
            <Route path="/zh/system-update-2026Q/v2" element={<main><SystemUpdate2026QV2Page /></main>} />

            {/* V2 預覽 - 訂單成功頁（殼換 V2，內容沿用 OrderSuccessPage；舊 /[lang]/order-success 保留不動） */}
            <Route path="/zh/order-success/v2" element={<main><OrderSuccessV2Page /></main>} />
            <Route path="/en/order-success/v2" element={<main><OrderSuccessV2Page /></main>} />

            {/* V2 預覽 - 訂單查詢／追蹤（殼換 V2，內容沿用 OrderTrackingPage） */}
            <Route path="/zh/order-tracking/v2" element={<main><OrderTrackingV2Page /></main>} />
            <Route path="/en/order-tracking/v2" element={<main><OrderTrackingV2Page /></main>} />

            {/* V2 預覽 - 藍新 NewebPay 付款成功回跳（殼換 V2，內容沿用 PaymentSuccessPage） */}
            <Route path="/zh/payment-success/v2" element={<main><PaymentSuccessV2Page /></main>} />
            <Route path="/en/payment-success/v2" element={<main><PaymentSuccessV2Page /></main>} />

            {/* V2 預覽 - 藍新 NewebPay 付款取消回跳（殼換 V2，內容沿用 PaymentCancelPage） */}
            <Route path="/zh/payment-cancel/v2" element={<main><PaymentCancelV2Page /></main>} />
            <Route path="/en/payment-cancel/v2" element={<main><PaymentCancelV2Page /></main>} />

            {/* V2 預覽 - 購物車（殼換 V2，內容沿用 CartPage；舊 /[lang]/cart 保留不動） */}
            <Route path="/zh/cart/v2" element={<main><CartV2Page /></main>} />
            <Route path="/en/cart/v2" element={<main><CartV2Page /></main>} />

            {/* V2 預覽 - 結帳流程（殼換 V2，內容沿用 CheckoutPage；藍新／EZShip 金流邏輯不動） */}
            <Route path="/zh/checkout/v2" element={<main><CheckoutV2Page /></main>} />
            <Route path="/en/checkout/v2" element={<main><CheckoutV2Page /></main>} />

            {/* 404 重定向到對應語言首頁 */}
            <Route path="*" element={<Navigate to="/zh" replace />} />
          </Routes>
        </div>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;
